"use client"

import { useState, useEffect } from "react"
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge } from "react-bootstrap"
import MapComponent from "../components/MapComponent"
import { apiRequest, endpoints } from "../utils/api"
import { toast } from "react-hot-toast"

const WeatherInformationPage = () => {
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [crops, setCrops] = useState([])
  const [selectedCrop, setSelectedCrop] = useState("")
  const [plantingDate, setPlantingDate] = useState(new Date().toISOString().split("T")[0])
//   const [weatherData, setWeatherData] = useState(null)
  const [growthData, setGrowthData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [cropsLoading, setCropsLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchCrops = async () => {
      try {
        setCropsLoading(true)
        const response = await apiRequest(endpoints.crops)
        const cropsList = Array.isArray(response) ? response : response.crops || []

        if (cropsList.length > 0) {
          setCrops(cropsList)
          // Set first crop as default
          setSelectedCrop(cropsList[0].name || cropsList[0].id)
        }
      } catch (err) {
        console.error("Failed to fetch crops:", err)
        const fallbackCrops = [
          { name: "tomato", id: "tomato" },
          { name: "wheat", id: "wheat" },
          { name: "corn", id: "corn" },
          { name: "cotton", id: "cotton" },
        ]
        setCrops(fallbackCrops)
        setSelectedCrop("tomato")
        toast.error("Using default crops - could not load from database")
      } finally {
        setCropsLoading(false)
      }
    }

    fetchCrops()
  }, [])

  const handleLocationSelect = (lat, lng, address) => {
    setSelectedLocation({ lat, lng, address })
  }

  const fetchGrowthData = async () => {
    if (!selectedLocation) {
      toast.error("Please select a location on the map")
      return
    }

    setLoading(true)
    setError("")

    try {
      const endDate = new Date(plantingDate)
      endDate.setDate(endDate.getDate() + 150) // 150 days analysis period
      const endDateStr = endDate.toISOString().split("T")[0]

      const response = await apiRequest(
        `${endpoints.weather}/crop-growth?lat=${selectedLocation.lat}&lng=${selectedLocation.lng}&cropName=${selectedCrop}&plantingDate=${plantingDate}&endDate=${endDateStr}`,
      )

      setGrowthData(response)
    } catch (err) {
      setError("Failed to fetch growth data: " + err.message)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container fluid className="py-4">
      <div className="mb-4">
        <h1 className="mb-2">🌤️ Weather Information & Crop Growth</h1>
        <p className="text-muted">
          Select a location to analyze weather and track crop growth stages using GDD calculations
        </p>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">📍 Select Location</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">Click on the map to select your location</p>
              <MapComponent onLocationSelect={handleLocationSelect} />
              {selectedLocation && (
                <div className="mt-3 p-3 bg-light rounded">
                  <p className="mb-1">
                    <strong>Selected Location:</strong>
                  </p>
                  <p className="mb-0">
                    <Badge bg="success">
                      {selectedLocation.address ||
                        `${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}`}
                    </Badge>
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">🌱 Crop Growth Configuration</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Select Crop</Form.Label>
                <Form.Select
                  value={selectedCrop}
                  onChange={(e) => setSelectedCrop(e.target.value)}
                  disabled={loading || cropsLoading}
                >
                  <option value="">{cropsLoading ? "Loading crops..." : "Choose a crop"}</option>
                  {crops.map((crop) => (
                    <option key={crop.id || crop.name} value={crop.name || crop.id}>
                      {(crop.name || crop.id).charAt(0).toUpperCase() + (crop.name || crop.id).slice(1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Planting Date</Form.Label>
                <Form.Control
                  type="date"
                  value={plantingDate}
                  onChange={(e) => setPlantingDate(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>

              <Button
                onClick={fetchGrowthData}
                disabled={!selectedLocation || loading || !selectedCrop}
                variant="success"
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Analyzing...
                  </>
                ) : (
                  "Analyze Growth"
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {growthData && (
        <Row>
          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">🌧️ Weather Summary & Forecast</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Total Rainfall</h6>
                      <h3 className="text-primary">
                        {Math.round(growthData.weatherSummary.totalRainfall * 10) / 10} mm
                      </h3>
                      <small>Over Period</small>
                    </div>
                  </Col>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Avg Humidity</h6>
                      <h3 className="text-success">{Math.round(growthData.weatherSummary.avgHumidity)}%</h3>
                      <small>Relative Humidity</small>
                    </div>
                  </Col>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Avg Temperature</h6>
                      <h3 className="text-warning">
                        {Math.round(growthData.weatherSummary.avgTemperature * 10) / 10}°C
                      </h3>
                      <small>Average</small>
                    </div>
                  </Col>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Data Source</h6>
                      <h3 className="text-secondary">
                        {growthData.dataSource === "synthetic" ? "Estimated" : "Historical"}
                      </h3>
                      <small>Weather Data</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">📊 Harvest Estimate</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Total GDD</h6>
                      <h3 className="text-primary">{Math.round(growthData.gddAnalysis.totalGDD)}</h3>
                      <small>Growing Degree Days</small>
                    </div>
                  </Col>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Progress</h6>
                      <h3 className="text-success">{growthData.harvestEstimate.progress}%</h3>
                      <small>To Maturity</small>
                    </div>
                  </Col>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Days to Harvest</h6>
                      <h3 className="text-info">{growthData.harvestEstimate.daysToHarvest}</h3>
                      <small>Estimated</small>
                    </div>
                  </Col>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <h6 className="text-muted mb-1">Harvest Date</h6>
                      <h3 className="text-warning">{growthData.harvestEstimate.estimatedHarvestDate}</h3>
                      <small>Estimated</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={12} className="mb-4">
            <Card>
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">🌾 Growth Stages & Weather Timeline</h5>
              </Card.Header>
              <Card.Body>
                {growthData.growthStages && growthData.growthStages.length > 0 && (
                  <div>
                    {(() => {
                      const latestStage = growthData.growthStages[growthData.growthStages.length - 1]
                      return (
                        <Row>
                          <Col md={6} className="mb-3">
                            <h6>Current Stage: {latestStage.stage.stage}</h6>
                            <p className="text-muted">{latestStage.stage.description}</p>
                            <div className="progress" style={{ height: "30px" }}>
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{ width: `${latestStage.stage.percentage}%` }}
                                aria-valuenow={latestStage.stage.percentage}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                {latestStage.stage.percentage}%
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="bg-light p-3 rounded">
                              <p className="mb-1">
                                <strong>Accumulated GDD:</strong> {Math.round(latestStage.accumulatedGDD)}
                              </p>
                              <p className="mb-1">
                                <strong>GDD Range:</strong> {latestStage.stage.gddRequired} - {latestStage.stage.gddMax}
                              </p>
                              <p className="mb-1">
                                <strong>Daily Rainfall:</strong> {Math.round(latestStage.rainfall * 10) / 10} mm
                              </p>
                              <p className="mb-0">
                                <strong>Humidity:</strong> {Math.round(latestStage.humidity)}%
                              </p>
                            </div>
                          </Col>
                        </Row>
                      )
                    })()}

                    <h6 className="mt-4 mb-3">📅 Daily Growth & Weather Forecast</h6>
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <div className="timeline">
                        {growthData.growthStages.map((stageData, index) => (
                          <div
                            key={index}
                            className="timeline-item mb-2 p-3 border rounded"
                            style={{ backgroundColor: "#f9f9f9" }}
                          >
                            <Row className="align-items-center">
                              <Col md={2} className="text-center">
                                <strong className="d-block">{stageData.date}</strong>
                                <small className="text-muted">{stageData.dailyTemp.toFixed(1)}°C</small>
                              </Col>
                              <Col md={2}>
                                <Badge bg="primary">{stageData.stage.stage}</Badge>
                              </Col>
                              <Col md={2} className="text-center">
                                <div>
                                  <small className="text-muted">GDD</small>
                                  <p className="mb-0">{Math.round(stageData.accumulatedGDD)}</p>
                                </div>
                              </Col>
                              <Col md={2} className="text-center">
                                <div>
                                  <small className="text-muted">Rain</small>
                                  <p className="mb-0">{Math.round(stageData.rainfall * 10) / 10} mm</p>
                                </div>
                              </Col>
                              <Col md={2} className="text-center">
                                <div>
                                  <small className="text-muted">Humidity</small>
                                  <p className="mb-0">{Math.round(stageData.humidity)}%</p>
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className="progress" style={{ height: "20px" }}>
                                  <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${stageData.stage.percentage}%` }}
                                    aria-valuenow={stageData.stage.percentage}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                    {stageData.stage.percentage}%
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  )
}

export default WeatherInformationPage
